import { PICKER_BROWSER_PACKAGE_MANAGER } from "@codesandbox/pitcher-client/dist/esm/browser";
import type { shell as shellProtocol } from "@codesandbox/pitcher-protocol";
import type {
  SandpackClient,
  SandpackMessage,
  UnsubscribeFunction,
} from "@codesandbox/sandpack-client";
import {
  getDependencyLatestVersion,
  getInstallationScript,
} from "components/Sidebar/DependencyManager/utils";
import { env } from "environment-interface/env";
import { useEditor } from "features/editor/Feature";
import { useAddStatusNotification } from "features/notifications/hooks";
import { usePreview } from "features/preview/Feature";
import type { Tab } from "features/types/tabs";
import { usePitcher } from "features/utils/usePitcher";
import { useEditorChannel } from "hooks/pitcher/useEditorChannel";
import { usePermissions } from "hooks/pitcher/usePermissions";
import { usePitcherHibernating } from "hooks/pitcher/usePitcherHibernating";
import type { PreviewRefCallback } from "hooks/preview/usePreviewProtocol";
import { usePreviewProtocol } from "hooks/preview/usePreviewProtocol";
import { withBasePath } from "isomorphic/utils";
import type { InjectFunction } from "preview-protocol";
import { Stack, styled, Text, Tooltip, Button, Box, Icon } from "prism-react";
import type { CSSProperties } from "react";
import { forwardRef, useEffect, useRef, useState } from "react";

import { Logo } from "components/Logo";
import { useAnalytics } from "hooks/useAnalytics";
import { useVisibility } from "hooks/useVisibility";
import { executeVSCodeCommand } from "utils/vscode";

import { DevtoolMessage } from "../common/DevtoolMessage";
import { DevtoolWrapper } from "../common/DevtoolWrapper";
import { IsolateDevtoolButton } from "../common/IsolateDevtoolButton";
import { ProcessErroredMessage } from "../common/ProcessErroredMessage";
import { ProcessKilledMessage } from "../common/ProcessKilledMessage";

import { InspectorButton } from "./InspectorButton";
import { NavigationBar } from "./NavigationBar";
import { injectAndInvokeInsidePreview } from "./preview-script";
import type {
  MessageFromDevToolToPreview,
  MessageFromPreviewToDevTool,
} from "./types";
import { useChromeDevtool } from "./useChromeDevtool";
import { useInspectorApi } from "./useInspectorApi";

export interface PreviewProps {
  shell: shellProtocol.CommandShellDTO | null;
  isPortActive: boolean;
  isPrimaryPort: boolean;
  disablePointerEvents: boolean;
  tab: Tab;
  panelId: string;
  canRestartProcess: boolean;
  killedBy?: string | null;
  onKillAction: () => void;
  secondaryActions?: JSX.Element[];
  /**
   * Optional, if we want to reduce the height by x pixels (because maybe something is rendered under the preview)
   * we can set it here.
   */
  heightOffset?: number;
  /**
   * Is only set if there's a task assigned to this preview
   */
  taskId?: string;
  onMountIframe?: (iframe: HTMLIFrameElement) => Promise<SandpackClient | null>;
  onConnected?: (
    injectAndInvoke: <Scope extends Record<string, unknown>>(
      func: InjectFunction<
        MessageFromDevToolToPreview,
        MessageFromPreviewToDevTool,
        Scope
      >,
      scope: Scope,
    ) => void,
  ) => void;
}

export const Preview: React.FC<PreviewProps> = ({
  tab,
  disablePointerEvents,
  isPortActive,
  isPrimaryPort,
  shell,
  killedBy,
  canRestartProcess,
  onKillAction,
  heightOffset = 0,
  taskId,
  secondaryActions,
  onMountIframe,

  onConnected,
}) => {
  const [preview, send] = usePreview();
  const lastPortActiveState = useRef(isPortActive);
  const isPitcherHibernating = usePitcherHibernating();
  const isShowingTab = useVisibility();
  const pitcherPermissions = usePermissions();
  const [browserDependencyNotFoundError, setBrowserDependencyNotFoundError] =
    useState<undefined | string>();

  const {
    status,
    previewRef,
    refresh,
    iframeRef,
    injectAndInvoke,
    onMessage,
    setIframeUrl,
    sendMessage,
  } = usePreviewProtocol<
    MessageFromDevToolToPreview,
    MessageFromPreviewToDevTool
  >(preview.url);

  const editor = useEditor();
  const inspectorApi = useInspectorApi(previewRef);
  const hasMountedRef = useRef(false);
  const isConnected = status === "CONNECTED";
  const shellExitCode = shell?.exitCode || null;
  const shellErrored =
    shell?.status === "ERROR" && shellExitCode && shellExitCode > 0;
  const shellKilled = shell?.status === "KILLED";
  const shellMissing = !shell;
  const isBrowserSandbox =
    editor.type === "sandbox" && editor.isCloud === false;
  const pitcher = usePitcher();
  const editorChannel = useEditorChannel();
  const [testRunnerAvailable, setTestRunnerAvailable] = useState(false);
  const track = useAnalytics();

  const [isResizing, setIsResizing] = useState(false);
  const [resizeSplit, setResizeSplit] = useState(0.5);
  const containerRef = useRef<HTMLDivElement>(null);

  // Start resizing
  useEffect(() => {
    if (!isResizing || !containerRef?.current) {
      return;
    }

    containerRef.current.querySelectorAll(`iframe`).forEach((item) => {
      (item as HTMLIFrameElement).style.pointerEvents = "none";
    });

    document.body.addEventListener("mousemove", onDragResize);
    document.body.addEventListener("mouseup", onStopDragging);

    return (): void => {
      document.body.removeEventListener("mousemove", onDragResize);
      document.body.removeEventListener("mouseup", onStopDragging);
    };
  }, [isResizing]);

  const onDragResize = (event: MouseEvent): void => {
    if (!containerRef?.current) return;

    const { top, height } = containerRef.current.getBoundingClientRect();
    const offset = (event.clientY - top) / height;
    const resizeSplit = Math.min(Math.max(offset, 0.2), 0.8);

    setResizeSplit(resizeSplit);
  };

  const onStopDragging = (): void => {
    if (!containerRef?.current) return;

    containerRef.current.querySelectorAll(`iframe`).forEach((item) => {
      (item as HTMLIFrameElement).style.pointerEvents = "";
    });
    setIsResizing(false);
  };

  const {
    devtoolVisible,
    devtoolIframeRef,
    consoleErrors,
    setDevToolVisibility,
    reloadDevtool,
  } = useChromeDevtool({
    isConnected,
    previewUrl: preview.url,
    onPreviewMessage: onMessage,
    sendMessageToPreview: sendMessage,
  });

  useEffect(
    () =>
      onMessage((message) => {
        if (
          message.type === "action" &&
          message.action === "show-error" &&
          message.title === "DependencyNotFoundError"
        ) {
          setBrowserDependencyNotFoundError(message.message);

          return;
        }
        if (message.type === "done" || message.type === "start") {
          setBrowserDependencyNotFoundError(undefined);
        }

        switch (message.type) {
          case "FOCUS_FILE": {
            const relativePath = pitcher.clients.fs.asRelativeWorkspacePath(
              message.filepath,
            );
            const line = message.currentLine;

            editorChannel?.send({
              type: "FOCUS_FILE",
              filepath: relativePath,
              visibleLines: [line, line],
              currentLine: line,
            });

            executeVSCodeCommand("codesandbox.openCsbTab", {
              type: "FILE",
              filepath: relativePath,
              initialSelections: [
                {
                  startLineNumber: line,
                  endLineNumber: line,
                  startColumn: 0,
                  endColumn: 0,
                },
              ],
            });
            break;
          }
          case "SET_URL":
            send({
              type: "TRACK",
              url: message.url,
              canGoBack: message.back,
              canGoForward: message.forward,
            });
            break;
        }
      }),
    [],
  );

  const unsubscribeSandpackListener = useRef<UnsubscribeFunction | undefined>(
    undefined,
  );
  function listenSandpackClientMessages(client: SandpackClient | null) {
    unsubscribeSandpackListener.current = client?.listen(
      (message: SandpackMessage) => {
        if (message.type === "test" && message.event === "test_count") {
          setTestRunnerAvailable(message.count > 0);
        }
      },
    );
  }
  useEffect(() => {
    return () => {
      unsubscribeSandpackListener.current?.();
    };
  }, []);

  useEffect(() => {
    // We know we are mounted when we have an asssigned iframe ref, but since this ref can change based on `src` changing, we
    // use a second ref to validate if we have already mounted
    if (previewRef.current && !hasMountedRef.current) {
      hasMountedRef.current = true;
      // We always have the previewRef when connected, or it would not be connected
      onMountIframe?.(previewRef.current).then(listenSandpackClientMessages);
    }
    // This is reactive and helps us make checks on the "non reactive" refs
  }, [isConnected]);

  useEffect(() => {
    if (isConnected) {
      injectAndInvoke(injectAndInvokeInsidePreview, {});
      onConnected?.(injectAndInvoke);
    }
  }, [isConnected]);

  useEffect(() => {
    if (isPortActive && !lastPortActiveState.current && previewRef.current) {
      const preview = previewRef.current;
      // We delay refreshing as the port can be available before the proxy knows
      const timeout = setTimeout(() => {
        preview.src = preview.src!;
      }, 500);

      lastPortActiveState.current = isPortActive;

      return () => clearTimeout(timeout);
    }

    lastPortActiveState.current = isPortActive;
  }, [isPortActive]);

  const isConnectedAndActive =
    // We do not want it to spin when having no shell or it is killed
    !shell || shell?.status === "KILLED"
      ? isConnected
      : // We want it to spin when port is pending or the iframe refreshes
        isPortActive && isConnected;

  const primaryPortNotAvailable = isPrimaryPort && !isPortActive;

  const onRefresh = () => {
    /**
     * Sandpack has its own protocol to reload the page
     */
    if (isBrowserSandbox && iframeRef) {
      iframeRef.contentWindow?.postMessage(
        { type: "refresh", codesandbox: true },
        "*",
      );
    } else {
      refresh();
      reloadDevtool();
      if (preview.iframeSrc.includes("#")) {
        sendMessage({ type: "REFRESH" });
      }
    }
  };

  const onGoBack = () => {
    reloadDevtool();
    sendMessage({ type: "GO_BACK" });
  };

  const onGoForward = () => {
    reloadDevtool();
    sendMessage({ type: "GO_FORWARD" });
  };

  const actions = [
    testRunnerAvailable ? (
      <Button
        key="tests"
        icon="play"
        onClick={() => {
          track("test_open_from_preview");
          executeVSCodeCommand("codesandbox.openCsbTab", { type: "TESTS" });
        }}
      >
        Run tests
      </Button>
    ) : (
      <></>
    ),

    <InspectorButton
      key="inspect-ai-button"
      inspectorApi={inspectorApi}
      isAiAvailable={pitcherPermissions.ai?.raw}
      isAiPrompt
    />,

    <InspectorButton key="inspect-button" inspectorApi={inspectorApi} />,

    "chrome" in window ? (
      <Tooltip key="chrome-devotol" content="Chrome DevTools for the preview">
        <Box css={{ position: "relative" }}>
          {!devtoolVisible && consoleErrors.length > 0 && (
            <Box
              css={{
                background: "$informative-error-base",
                width: 5,
                height: 5,
                borderRadius: "50%",
                position: "absolute",
                right: "$1",
                top: "$1",
              }}
            />
          )}
          <Button
            icon="addTerminal"
            onClick={() => setDevToolVisibility((prev) => !prev)}
          />
        </Box>
      </Tooltip>
    ) : (
      <Tooltip
        key="chrome-devotol"
        content="The Chrome DevTools for the preview are enabled for Chrome browsers"
      >
        <Box css={{ position: "relative" }}>
          <Button
            icon="addTerminal"
            onClick={() => setDevToolVisibility((prev) => !prev)}
            disabled
          />
        </Box>
      </Tooltip>
    ),

    <IsolateDevtoolButton
      key="isolate"
      id={tab.id}
      onClose={() => {}}
      onOpen={() => {}}
      popupUrl={withBasePath(env, `/devtool/preview?path=${preview.url}`)}
      url={preview.url}
    />,
  ];

  return (
    <DevtoolWrapper variant="preview">
      <Stack
        gap={0}
        style={{ height: `calc(100% - ${heightOffset}px)`, width: "100%" }}
      >
        <NavigationBar
          actions={actions}
          isConnected={isConnectedAndActive}
          onGoBack={onGoBack}
          onGoForward={onGoForward}
          onRefresh={onRefresh}
          secondaryActions={secondaryActions}
          setIframeUrl={(url: string) => {
            setIframeUrl(url);
          }}
        />

        <Box
          ref={containerRef}
          css={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <Box css={{ flex: devtoolVisible ? resizeSplit : 1 }}>
            <InnerPreview
              browserDependencyNotFoundError={browserDependencyNotFoundError}
              disablePointerEvents={disablePointerEvents}
              isBrowserSandbox={isBrowserSandbox}
              isPitcherHibernating={isPitcherHibernating}
              isPortActive={isPortActive}
              isShowingTab={isShowingTab}
              previewRef={previewRef}
            />
          </Box>

          {devtoolVisible && (
            <>
              <Box
                css={{
                  height: "1px",
                  width: "100%",
                  background: "$neutral-bg-high",
                  position: "relative",
                }}
                onMouseDown={() => setIsResizing(true)}
              >
                <Box
                  css={{
                    position: "absolute",
                    top: "-2px",
                    height: "4px",
                    width: "100%",
                    cursor: "row-resize",
                    background: "transparent",
                    transition: "background 0.1s ease",
                    transitionDelay: "0.4s",
                    "&:hover": {
                      background: "$accent-secondary-base",
                    },
                  }}
                />
              </Box>
              <Iframe
                ref={devtoolIframeRef}
                css={{
                  flex: 1 - resizeSplit,
                }}
                src={withBasePath(
                  env,
                  `/chrome-devtool/app/index.html#?embedded=${encodeURIComponent(
                    window.location.origin,
                  )}`,
                )}
                title="Chrome devtool"
              />
            </>
          )}
        </Box>

        {shellErrored && (
          <ProcessErroredMessage
            canRestartProcess={canRestartProcess}
            exitCode={shellExitCode}
            onAction={onKillAction}
            processType="TASK"
          />
        )}

        {(shell?.status === "RUNNING" || shell?.status === "FINISHED") &&
          primaryPortNotAvailable &&
          canRestartProcess && (
            <DevtoolMessage
              actions={[
                {
                  actionText: "Open task",
                  onAction: () => {
                    if (!taskId) {
                      // Should always be available
                      return;
                    }

                    executeVSCodeCommand("codesandbox.terminal.open", {
                      taskId,
                    });
                  },
                },
              ]}
              variant="info"
            >
              <Text>
                This task of this preview has a port configured, but it's not
                opened
              </Text>
            </DevtoolMessage>
          )}

        {(shellKilled || (shellMissing && !isPortActive)) && (
          <ProcessKilledMessage
            canRestartProcess={canRestartProcess}
            killedBy={killedBy}
            onAction={onKillAction}
            processType={shell ? "TASK" : "UNASSIGNED_PORT"}
          />
        )}
      </Stack>
    </DevtoolWrapper>
  );
};

interface InnerPreviewProps {
  isPitcherHibernating: boolean;
  isShowingTab: boolean;
  isBrowserSandbox: boolean;
  previewRef: PreviewRefCallback;
  disablePointerEvents: boolean;
  isPortActive: boolean;
  browserDependencyNotFoundError?: string;
}

const InnerPreview: React.FC<InnerPreviewProps> = ({
  isPitcherHibernating,
  isShowingTab,
  isBrowserSandbox,
  previewRef,
  disablePointerEvents,
  isPortActive,
  browserDependencyNotFoundError,
}) => {
  const [preview] = usePreview();
  const [running, setRunning] = useState(
    !document.location.search.includes("runonclick=1"),
  );

  if (!running) {
    return (
      <Stack
        css={{
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
        gap={4}
      >
        <Logo css={{ color: "$neutral-bg-medium" }} />
        <Text>Preview is not running</Text>
        <Button icon="run" onClick={() => setRunning(true)} variant="secondary">
          Click to run
        </Button>
      </Stack>
    );
  }

  if (isPitcherHibernating && !isShowingTab) {
    return (
      <Iframe
        css={{ background: "$neutral-bg-base" }}
        title="Hibernating preview"
      />
    );
  }

  if (isBrowserSandbox) {
    return (
      <>
        {browserDependencyNotFoundError && (
          <MissingDependencyErrorOverlay
            message={browserDependencyNotFoundError}
          />
        )}

        <SandpackIframe
          ref={previewRef}
          ignorePointerEvents={disablePointerEvents}
          src={preview.iframeSrc}
          // When there is a dependency error when only hide the iframe
          // because we need to keep the bundler running in the background
          style={{ display: browserDependencyNotFoundError ? "none" : "block" }}
        />
      </>
    );
  }

  return (
    <CloudIframe
      ref={previewRef}
      ignorePointerEvents={disablePointerEvents}
      isPortActive={isPortActive}
      src={preview.iframeSrc}
    />
  );
};

const MissingDependencyErrorOverlay: React.FC<{ message: string }> = ({
  message,
}) => {
  const pitcher = usePitcher();
  const addNotification = useAddStatusNotification();
  const track = useAnalytics();

  const errorMessage = message.match(/'(.*?)'/);
  const dependencyName = errorMessage?.[1];

  if (!dependencyName) {
    return;
  }

  const installDep = async () => {
    try {
      const latest = await getDependencyLatestVersion(dependencyName);

      track("install_missing_dep");

      const command = getInstallationScript({
        manager: PICKER_BROWSER_PACKAGE_MANAGER,
        dependency: dependencyName,
        version: latest,
        dev: false,
      });

      await pitcher.clients.task.runCommand(
        command,
        false,
        `Upgrading ${dependencyName}`,
      );
    } catch {
      addNotification({
        status: "error",
        message: `Could not install ${dependencyName}`,
      });
    }
  };

  return (
    <Stack
      css={{
        padding: "$10",
        margin: "auto",
        maxWidth: 500,
        height: "100%",
      }}
      gap={4}
    >
      <Stack
        css={{ alignItems: "center", color: "$informative-error-subtle" }}
        horizontal
      >
        <Icon size={5} type="circleBang" />
        <Text size="sm">Dependency not found</Text>
      </Stack>

      <Text as="pre" css={{ whiteSpace: "pre-wrap", textWrap: "balance" }}>
        {message}
      </Text>

      <Box>
        <Button icon="terminal" onClick={installDep} variant="secondary">
          Install "{dependencyName}"
        </Button>
      </Box>
    </Stack>
  );
};

const CloudIframe = forwardRef<
  HTMLIFrameElement,
  {
    src: string;
    ignorePointerEvents: boolean;
    isPortActive: boolean;
  }
>(({ ignorePointerEvents, src, isPortActive }, ref) => {
  return (
    <Iframe
      ref={ref}
      allow="clipboard-read; clipboard-write; accelerometer; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; xr-spatial-tracking; cross-origin-isolated"
      css={{
        background: "white",
        transition: "opacity $fast",
        opacity: isPortActive ? 1 : 0.1,
      }}
      sandbox="allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
      src={src}
      style={{ pointerEvents: ignorePointerEvents ? "none" : undefined }}
      title={`Preview for ${src}`}
    />
  );
});

const SandpackIframe = forwardRef<
  HTMLIFrameElement,
  { src: string; ignorePointerEvents: boolean; style: CSSProperties }
>(({ ignorePointerEvents, src, style = {} }, ref) => (
  <Iframe
    ref={ref}
    css={{ background: "white" }}
    src={src}
    style={{
      pointerEvents: ignorePointerEvents ? "none" : undefined,
      ...style,
    }}
    title={`Preview for ${src}`}
  />
));

const Iframe = styled("iframe", {
  display: "block",
  border: "0",
  height: "100%",
  width: "100%",
});
